import React, { ChangeEvent, useLayoutEffect, useRef } from 'react';
import { FormGroup, Input } from 'reactstrap';

import InputLabel from './InputLabel';

import 'style/layout/form/ContentSizedTextarea.css';

const ContentSizedTextarea = ({
  className,
  disabled,
  id,
  label,
  name,
  onChange,
  value,
}: {
  className?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  name?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
}): JSX.Element => {
  const ref = useRef(null);

  const resize = () => {
    const currentRef: any = ref.current;
    if (currentRef === null) {
      return;
    }

    // Skip while the field is hidden (e.g. an inactive tab/collapsed panel):
    // scrollHeight is 0 when the element isn't laid out, which would otherwise
    // lock the textarea to a collapsed height until the next keystroke.
    if (
      currentRef.offsetParent === null &&
      currentRef.getClientRects().length === 0
    ) {
      return;
    }

    // Collapse to 'auto' first so scrollHeight reflects the true content
    // height. scrollHeight never reports less than the element's current
    // height, so measuring before collapsing would lock in a stale size.
    currentRef.style.height = 'auto';
    const newValue = `${currentRef.scrollHeight}px`;
    if (newValue !== currentRef.style.height) {
      currentRef.style.height = newValue;
    }
  };

  // Re-measure after every render (mount, value changes from typing).
  useLayoutEffect(() => {
    resize();
  });

  // Re-measure whenever the element's box actually changes — becoming visible
  // when its container is shown, container resizes, or a web font finishing
  // loading. Without this the height stays stuck at whatever it was when the
  // field was first (often invisibly) measured.
  useLayoutEffect(() => {
    const currentRef: any = ref.current;
    // ResizeObserver isn't in this project's TypeScript (3.9) DOM types, so it
    // is reached through window; it exists in every browser we support.
    const ResizeObserverCtor: any = (window as any).ResizeObserver;
    if (currentRef === null || ResizeObserverCtor === undefined) {
      return undefined;
    }

    const observer = new ResizeObserverCtor(() => resize());
    observer.observe(currentRef);

    return () => observer.disconnect();
  }, []);

  const getClassName = () => {
    const classes = ['content-sized-textarea'];

    if (className !== undefined) {
      classes.push(className);
    }

    return classes.join(' ');
  };

  return (
    <FormGroup>
      {label === undefined ? null : <InputLabel>{label}</InputLabel>}
      <Input
        className={getClassName()}
        disabled={disabled}
        id={id}
        innerRef={ref}
        name={name}
        onChange={onChange}
        type="textarea"
        value={value}
      />
    </FormGroup>
  );
};

export default ContentSizedTextarea;
